import { z } from 'zod';

/**
 * How several applicable grants combine on one order (matrix rows 11–13).
 *
 * The policy is a property of the **rule**, and a member can hold grants from
 * several rules at once — so an order's effective policy is resolved from the
 * grants in play rather than read off one of them. {@link STACKING_STRICTNESS}
 * is what makes that resolution total instead of order-dependent.
 *
 * **`none` and `best_of` compute the same automatic answer, and that is not a
 * redundancy.** Both yield a single grant, and with nothing else to go on that
 * grant is the highest-value one — row 11 says "chosen **or** best". They part
 * company at order entry (P23), where `best_of` may be overridden by a member
 * picking a different one of their grants and `none` may not be combined with
 * anything under any circumstances. Collapsing them here would delete a
 * distinction billing still has to honour.
 */
export const STACKING_POLICIES = ['none', 'best_of', 'stack'] as const;

export const StackingPolicySchema = z.enum(STACKING_POLICIES);

export type StackingPolicy = (typeof STACKING_POLICIES)[number];

const POLICY_SET: ReadonlySet<string> = new Set(STACKING_POLICIES);

export function isStackingPolicy(value: string): value is StackingPolicy {
  return POLICY_SET.has(value);
}

/**
 * How restrictive each policy is — **higher wins** when grants disagree.
 *
 * A member holding a `none` grant and a `stack` grant must not get to pick the
 * permissive one by ordering their basket differently, and the operator who set
 * `none` on a rule meant "this one is never combined". So the strictest policy
 * present governs the whole order, and the resolution is a `max` rather than a
 * scan with an early return.
 */
export const STACKING_STRICTNESS: Readonly<Record<StackingPolicy, number>> = {
  stack: 0,
  best_of: 1,
  none: 2,
};
